import React from 'react';
import {
  Create,
  Datagrid,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  required,
  email,
  SelectInput,
  Show,
  SimpleShowLayout,
  TopToolbar,
  EditButton,
  ShowButton,
  CreateButton,
  useTranslate,
  RecordContextProvider,
  ExportButton,
  FilterButton,
  FilterForm,
  SearchInput,
  ArrayInput,
  SimpleFormIterator,
  useRecordContext,
  BooleanInput,
  NumberInput,
  BooleanField,
  FormDataConsumer,
  CheckboxGroupInput,
} from 'react-admin';
import { Box, Button, Card, CardContent, Typography, Chip, Stack, Tooltip } from '@mui/material';
import { PhoneContact } from '../types';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/** The partner-facing portal lives in aura-app, under its /app basePath. */
const buildPortalUrl = (partnerId: string | number): string => {
  const fallback =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:3010'
      : 'https://cleocremation.com';
  const auraAppUrl = (process.env.REACT_APP_AURA_APP_URL || fallback).replace(/\/$/, '');
  return `${auraAppUrl}/app/partners/${partnerId}`;
};

/**
 * Opens this partner's own portal page.
 *
 * Only the three roles that actually have a view get the button. Every partner
 * id resolves to a page, but a transport-only company would land on the
 * crematorium view filtered by its own id and always see an empty schedule --
 * a link that looks broken rather than one that is absent.
 */
const PartnerPortalButton = () => {
  const record = useRecordContext();
  const hasPortal =
    !!record &&
    (record.is_crematorium || record.is_dropoff_location || record.is_answering_service);
  if (!hasPortal) return null;

  return (
    <Tooltip title="Open this partner's portal page in a new tab">
      <Button
        size="small"
        startIcon={<OpenInNewIcon />}
        href={buildPortalUrl(record.id)}
        target="_blank"
        rel="noopener noreferrer"
        // The datagrid row is itself a link to the show view.
        onClick={(e) => e.stopPropagation()}
      >
        Portal
      </Button>
    </Tooltip>
  );
};

const validateAccessPin = (value?: string) =>
  !value || /^\d{4}$/.test(value) ? undefined : 'Must be exactly 4 digits';

// Routing/agreement config — only relevant for crematorium partners. This is the
// settings surface for the QC Cardinal/Rideau split.
const CrematoriumRoutingInputs = () => (
  <FormDataConsumer>
    {({ formData }) =>
      formData?.is_crematorium ? (
        <Box sx={{ mt: 1, mb: 1, width: '100%' }}>
          <Typography variant="subtitle2" gutterBottom>
            Crematorium routing
          </Typography>
          <SelectInput
            source="agreement_type"
            label="Cremation agreement"
            helperText="Which agreement is sent for cases routed here (blank = region default)"
            choices={[
              { id: 'rideau', name: 'Rideau (RETO)' },
              { id: 'cleo', name: 'Cleo' },
              { id: 'southwest', name: 'Southwest' },
            ]}
          />
          <BooleanInput
            source="is_default"
            label="Default crematorium for this agreement"
            helperText="Filled in on new cases when several crematoriums share the agreement (e.g. the Southwest locations). One per agreement."
            defaultValue={false}
          />
          <BooleanInput
            source="accepts_visitation"
            label="Accepts visitation cases"
            defaultValue={true}
          />
          <BooleanInput
            source="routing_active"
            label="Include in monthly routing split"
            defaultValue={true}
          />
          <NumberInput
            source="monthly_target"
            label="Monthly target (cases/month)"
            min={0}
            defaultValue={0}
            helperText="Eligible at-need QC cases to route here, paced evenly across the month. 0 = none."
          />
          <CheckboxGroupInput
            source="canada_post_pickup_weekdays"
            label="Canada Post pickup days"
            helperText="A family confirming Canada Post shipping gets the next of these days as their ship day (holidays skipped). None = staff set the day."
            choices={[
              { id: 1, name: 'Mon' },
              { id: 2, name: 'Tue' },
              { id: 3, name: 'Wed' },
              { id: 4, name: 'Thu' },
              { id: 5, name: 'Fri' },
            ]}
          />
          <TextInput
            source="default_operator_name"
            label="Default operator name"
            fullWidth
            helperText="Pre-fills the operator signature line on the cremation certificate (leave blank to hand-write)."
          />
        </Box>
      ) : null
    }
  </FormDataConsumer>
);

// Roles are independent capabilities — a partner can hold several at once
// (e.g. a transport company whose facility is also a drop-off location).
const PARTNER_ROLES: { source: string; label: string }[] = [
  { source: 'is_transport', label: 'Transport' },
  { source: 'is_crematorium', label: 'Crematorium' },
  { source: 'is_dropoff_location', label: 'Drop-off location' },
  { source: 'is_answering_service', label: 'Answering service' },
  // Families may collect ashes here. The delivery editor offers exactly these
  // partners, narrowed to the case's region — our Montréal office in Quebec,
  // the Southwest locations in Ontario.
  { source: 'is_ashes_pickup_location', label: 'Ashes pickup location' },
];

const REGION_CHOICES = [
  { id: 'quebec', name: 'Quebec' },
  { id: 'ontario', name: 'Ontario' },
];

const PartnerRoleInputs = () => (
  <Box sx={{ mt: 1, mb: 1, width: '100%' }}>
    <SelectInput
      source="region"
      label="Region"
      choices={REGION_CHOICES}
      helperText="Market this partner serves — funeral dropdowns only offer partners in the case's region (blank = shown everywhere)"
    />
    <Typography variant="subtitle2" gutterBottom>
      Roles
    </Typography>
    {PARTNER_ROLES.map((role) => (
      <BooleanInput key={role.source} source={role.source} label={role.label} />
    ))}
  </Box>
);

const PartnerRolesField = () => {
  const record = useRecordContext();
  if (!record) return null;
  const roles = PARTNER_ROLES.filter((role) => record[role.source]);
  if (roles.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roles.map((role) => (
        <Chip key={role.source} label={role.label} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

PartnerRolesField.defaultProps = { label: 'Roles' };

const PartnerRegionField = () => {
  const record = useRecordContext();
  if (!record?.region) return null;
  const label = REGION_CHOICES.find((c) => c.id === record.region)?.name ?? record.region;
  return <Chip label={label} size="small" />;
};

PartnerRegionField.defaultProps = { label: 'Region' };

const partnerFilters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput source="region" label="Region" choices={REGION_CHOICES} />,
  <BooleanInput source="is_transport" label="Transport" />,
  <BooleanInput source="is_crematorium" label="Crematorium" />,
  <BooleanInput source="is_dropoff_location" label="Drop-off location" />,
  <BooleanInput source="is_ashes_pickup_location" label="Ashes pickup location" />,
];

// Custom validator for comma-separated emails
const validateEmails = (value: string | undefined) => {
  if (!value) return undefined;
  
  // Split by commas and trim each email
  const emails = value.split(',').map((email: string) => email.trim()).filter(Boolean);
  
  // Validate each email
  for (const emailValue of emails) {
    const error = email()(emailValue);
    if (error) {
      return `"${emailValue}" is not a valid email`;
    }
  }
  
  return undefined;
};

// Display component for phone numbers
const PhoneNumbersField = () => {
  const record = useRecordContext();
  if (!record) return null;

  // Check if phones array exists and has entries
  if (record.phones && Array.isArray(record.phones) && record.phones.length > 0) {
    return (
      <Stack direction="column" spacing={1}>
        {record.phones.map((phone: PhoneContact, index: number) => (
          <Chip 
            key={index}
            icon={<PhoneIcon />}
            label={`${phone.label}: ${phone.number}`}
            variant="outlined"
            size="small"
          />
        ))}
      </Stack>
    );
  }

  // Fallback to main phone number if no phones array
  return <TextField source="phone" />;
};

PhoneNumbersField.defaultProps = { label: 'Phone Numbers' };

// Display component for email addresses
const EmailField = () => {
  const record = useRecordContext();
  if (!record || !record.email) return null;
  
  // Check if there are multiple emails (comma-separated)
  if (record.email.includes(',')) {
    const emails = record.email.split(',').map((email: string) => email.trim()).filter(Boolean);
    return (
      <Stack direction="column" spacing={1}>
        {emails.map((email: string, index: number) => (
          <Chip 
            key={index}
            icon={<EmailIcon />}
            label={email}
            variant="outlined"
            size="small"
          />
        ))}
      </Stack>
    );
  }
  
  // Single email
  return <TextField source="email" />;
};

EmailField.defaultProps = { label: 'Email' };

export const PartnerList = () => {
  const translate = useTranslate();
  
  return (
    <List 
      filters={partnerFilters}
      sort={{ field: 'name', order: 'ASC' }}
      actions={
        <TopToolbar>
          <FilterButton />
          <CreateButton />
          <ExportButton />
        </TopToolbar>
      }
    >
      <Datagrid>
        <TextField source="name" />
        <PartnerRegionField />
        <PartnerRolesField />
        <PhoneNumbersField />
        <EmailField />
        <TextField source="city" />
        <TextField source="province" />
        <PartnerPortalButton />
        <ShowButton />
        <EditButton />
      </Datagrid>
    </List>
  );
};

export const PartnerCreate = () => {
  return (
    <Create>
      <SimpleForm>
        <TextInput source="name" label="Display name" validate={required()} fullWidth helperText="Short name used across the admin, dispatch, and partner portal (e.g. Rideau)." />
        <TextInput source="legal_name" label="Legal name (for documents)" fullWidth helperText="Official registered name shown on generated documents (blank = use display name)." />
        <PartnerRoleInputs />
        <TextInput source="phone" fullWidth helperText="Main phone number" />
        
        <ArrayInput source="phones" label="Additional Phone Numbers">
          <SimpleFormIterator>
            <TextInput source="label" helperText="e.g., Office, Mobile, After Hours" />
            <TextInput source="number" />
          </SimpleFormIterator>
        </ArrayInput>
        
        <TextInput source="email" validate={validateEmails} fullWidth helperText="Multiple emails can be entered using comma-separation, e.g. email1@example.com, email2@example.com" />
        <TextInput source="prep_sheet_emails" fullWidth helperText="Default recipients for prep sheet emails, comma-separated" />
        <TextInput source="address" fullWidth />
        <TextInput source="city" />
        <TextInput source="province" />
        <TextInput source="postal_code" />
        <CrematoriumRoutingInputs />
        <BooleanInput
          source="require_pin"
          label="Require access PIN"
          defaultValue={true}
          helperText="When off, this partner's portal page opens without a PIN (e.g. a staffed answering service)."
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData.require_pin === false ? null : (
              <TextInput
                source="access_pin"
                label="Access PIN"
                validate={validateAccessPin}
                inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                helperText="This partner's own 4-digit code. Leave blank to use the shared PIN."
              />
            )
          }
        </FormDataConsumer>
        <TextInput source="notes" multiline rows={4} fullWidth />
      </SimpleForm>
    </Create>
  );
};

export const PartnerEdit = () => {
  return (
    <Edit>
      <SimpleForm>
        <TextInput source="name" label="Display name" validate={required()} fullWidth helperText="Short name used across the admin, dispatch, and partner portal (e.g. Rideau)." />
        <TextInput source="legal_name" label="Legal name (for documents)" fullWidth helperText="Official registered name shown on generated documents (blank = use display name)." />
        <PartnerRoleInputs />
        <TextInput source="phone" fullWidth helperText="Main phone number" />
        
        <ArrayInput source="phones" label="Additional Phone Numbers">
          <SimpleFormIterator>
            <TextInput source="label" helperText="e.g., Office, Mobile, After Hours" />
            <TextInput source="number" />
          </SimpleFormIterator>
        </ArrayInput>
        
        <TextInput source="email" validate={validateEmails} fullWidth helperText="Multiple emails can be entered using comma-separation, e.g. email1@example.com, email2@example.com" />
        <TextInput source="prep_sheet_emails" fullWidth helperText="Default recipients for prep sheet emails, comma-separated" />
        <TextInput source="address" fullWidth />
        <TextInput source="city" />
        <TextInput source="province" />
        <TextInput source="postal_code" />
        <CrematoriumRoutingInputs />
        <BooleanInput
          source="require_pin"
          label="Require access PIN"
          defaultValue={true}
          helperText="When off, this partner's portal page opens without a PIN (e.g. a staffed answering service)."
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData.require_pin === false ? null : (
              <TextInput
                source="access_pin"
                label="Access PIN"
                validate={validateAccessPin}
                inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                helperText="This partner's own 4-digit code. Leave blank to use the shared PIN."
              />
            )
          }
        </FormDataConsumer>
        <TextInput source="notes" multiline rows={4} fullWidth />
      </SimpleForm>
    </Edit>
  );
};

// Component to display phone numbers in the show view
const PhoneNumbersDetail = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Main Phone:</Typography>
      <Typography paragraph>{record.phone || 'Not provided'}</Typography>
      
      {record.phones && Array.isArray(record.phones) && record.phones.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>Additional Phone Numbers:</Typography>
          <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
            {record.phones.map((phone: PhoneContact, index: number) => (
              <Box key={index} display="flex" alignItems="center" sx={{ mb: 1 }}>
                <PhoneIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }}/>
                <Typography variant="body2">
                  <strong>{phone.label}:</strong> {phone.number}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

PhoneNumbersDetail.defaultProps = { label: 'Phone Numbers' };

// Component to display email addresses in the show view
const EmailsDetail = () => {
  const record = useRecordContext();
  if (!record || !record.email) return null;

  // Check if there are multiple emails
  if (record.email.includes(',')) {
    const emails = record.email.split(',').map((email: string) => email.trim()).filter(Boolean);
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>Emails:</Typography>
        <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
          {emails.map((email: string, index: number) => (
            <Box key={index} display="flex" alignItems="center" sx={{ mb: 1 }}>
              <EmailIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }}/>
              <Typography variant="body2">{email}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  // Single email
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Email:</Typography>
      <Box display="flex" alignItems="center">
        <EmailIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }}/>
        <Typography variant="body2">{record.email}</Typography>
      </Box>
    </Box>
  );
};

EmailsDetail.defaultProps = { label: 'Email' };

export const PartnerShow = () => {
  return (
    <Show
      actions={
        <TopToolbar>
          <EditButton />
        </TopToolbar>
      }
    >
      <SimpleShowLayout>
        <TextField source="name" label="Display name" />
        <TextField source="legal_name" label="Legal name (for documents)" />
        <PartnerRegionField />
        <PartnerRolesField />
        <PhoneNumbersDetail />
        <EmailsDetail />
        <TextField source="address" />
        <TextField source="city" />
        <TextField source="province" />
        <TextField source="postal_code" />
        <TextField source="agreement_type" label="Cremation agreement" />
        <BooleanField source="accepts_visitation" label="Accepts visitation" />
        <BooleanField source="routing_active" label="In routing split" />
        <TextField source="monthly_target" label="Monthly target" />
        <TextField source="default_operator_name" label="Default operator name" />
        <BooleanField source="require_pin" label="Require access PIN" />
        <TextField source="access_pin" label="Access PIN (blank = shared)" />
        <TextField source="notes" />
      </SimpleShowLayout>
    </Show>
  );
};

const Partners = {
  list: PartnerList,
  create: PartnerCreate,
  edit: PartnerEdit,
  show: PartnerShow,
};

export default Partners; 